import React, { useMemo } from "react";
import { Box, Button, Card, Chip, Stack, Typography } from "@mui/material";

function formatConversion(conversionRate) {
  if (conversionRate == null || Number.isNaN(Number(conversionRate))) return "—";
  return `${Math.round(Number(conversionRate) * 1000) / 10}%`;
}

export default function FunnelStageCard({
  stepNumber,
  label,
  description,
  example,
  configured,
  volume,
  conversionRate,
  volumeLabel = "Volume",
  conversionLabel = "Conversão",
  onClick,
  compact = false,
}) {
  const conversionText = useMemo(() => formatConversion(conversionRate), [conversionRate]);

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        cursor: onClick ? "pointer" : "default",
        transition: "border-color 140ms ease, box-shadow 140ms ease",
        "&:hover": onClick
          ? { borderColor: "primary.light", boxShadow: 2 }
          : undefined,
      }}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (!onClick) return;
        if (e.key === "Enter" || e.key === " ") onClick?.();
      }}
    >
      <Box sx={{ p: { xs: 2.25, sm: 2.75 }, flex: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
            {stepNumber != null ? (
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  bgcolor: configured ? "primary.main" : "action.selected",
                  color: configured ? "primary.contrastText" : "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 700,
                  flexShrink: 0,
                  mt: 0.15,
                }}
              >
                {stepNumber}
              </Box>
            ) : null}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, lineHeight: 1.3 }}>{label}</Typography>
              {!compact && description ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.5 }}>
                  {description}
                </Typography>
              ) : null}
              {!compact && example ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                  {example}
                </Typography>
              ) : null}
            </Box>
          </Stack>
          <Chip
            size="small"
            label={configured ? "Pronta" : "Pendente"}
            color={configured ? "success" : "default"}
            variant={configured ? "filled" : "outlined"}
            sx={{ flexShrink: 0 }}
          />
        </Stack>

        <Stack
          direction="row"
          spacing={2.5}
          sx={{
            mt: 2.5,
            pt: 2,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {volumeLabel}
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              {volume ?? "—"}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {conversionLabel}
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              {conversionText}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {onClick ? (
        <Box
          sx={{
            px: { xs: 2.25, sm: 2.75 },
            py: 1.75,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "action.hover",
          }}
        >
          <Button
            size="small"
            variant={configured ? "outlined" : "contained"}
            fullWidth
            onClick={(e) => {
              e.stopPropagation();
              onClick?.();
            }}
          >
            {configured ? "Ajustar etapa" : "Configurar etapa"}
          </Button>
        </Box>
      ) : null}
    </Card>
  );
}
